import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import NativeSelect from '@material-ui/core/NativeSelect';
import Button from '@material-ui/core/Button';
import 'react-datepicker/dist/react-datepicker.css';
import * as actionTypes from '~/store/actions/actionTypes';
import { filterOptions } from '~/store/reducers/historyFilterOptions';
import { colors } from '~/layout';
import { FormControl } from '../sharedStyles';
import {
  DatePickerCustomHeader,
  CalendarFooterContainer,
  TextField,
} from './styles';

import './styles.css';

function subDays(date: Date, days: number): Date {
  if (date) {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  return new Date();
}

function addDays(date: Date, days: number): Date {
  if (date) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }
  return new Date();
}

function transformDateToAPIStandards(inputDate: any, isStart: boolean = false) {
  const parsedDate = new Date(inputDate);
  if (isStart) {
    parsedDate.setHours(0, 0, 0, 0);
  } else {
    parsedDate.setHours(23, 59, 59, 0);
  }
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  const hours = String(parsedDate.getHours()).padStart(2, '0');
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0');
  const seconds = String(parsedDate.getSeconds()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  return formattedDate;
}

function getLastYearsList() {
  const currentYear = new Date().getFullYear();
  const startYear = 2009;

  const yearsList = [];
  for (let year = currentYear; year >= startYear; year -= 1) {
    yearsList.push(year);
  }

  return yearsList;
}

function getMonths() {
  const moments = [];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  for (let number = 0; number < months.length; number += 1) {
    const shortName = months[number].substring(0, 3);
    const longName = months[number];

    moments.push({ shortName, longName, number });
  }

  const momentsNotRef = moments.map(moment => ({ ...moment }));
  return momentsNotRef;
}

export default function CalendarRangeSelector() {
  const dispatch = useDispatch();

  const yearsList = getLastYearsList();
  const [currentYear] = yearsList;

  const months = getMonths();

  const [startMonth, setStartMonth] = useState(undefined);
  const [endMonth, setEndMonth] = useState(undefined);

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState<any>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (startDate && endDate !== null) {
      dispatch({
        type: actionTypes.SET_HISTORY_FILTER_OPTIONS,
        option: filterOptions.dateRange,
        payload: {
          start: transformDateToAPIStandards(startDate, true),
          end: transformDateToAPIStandards(endDate),
        },
        extra: { resetCallItems: true, hasDateRange: true },
      });
    }
  }, [startDate, endDate, dispatch]);

  const onChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const selectToday = () => {
    const todayStarts = new Date();
    todayStarts.setHours(0, 0, 0, 0);
    setStartDate(todayStarts);

    const todayEnds = new Date();
    todayEnds.setHours(23, 59, 59, 0);
    setEndDate(todayEnds);
  };

  const selectThisMonth = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const curYear = now.getFullYear();
    const curMonth = now.getMonth();

    const startMonthDate = new Date(curYear, curMonth, 1);

    const endMonthDate = new Date();
    endMonthDate.setHours(23, 59, 59, 0);

    setStartDate(startMonthDate);
    setEndDate(endMonthDate);
  };

  const selectThisYear = () => {
    setStartDate(new Date(new Date().getFullYear(), 0, 1));
    setEndDate(new Date());
  };

  const CalendarContainerComponent = ({
    className,
    children,
  }: {
    className: any;
    children: any;
  }) => {
    return (
      <CalendarFooterContainer>
        <div className={className}>
          <div>{children}</div>
          <div className="calendar-buttons">
            <Button
              disableRipple
              variant="outlined"
              onClick={() => selectToday()}>
              Today
            </Button>
            <Button variant="outlined" onClick={() => selectThisMonth()}>
              This Month
            </Button>
            <Button variant="outlined" onClick={() => selectThisYear()}>
              This Year
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpen(false)}>
              Save
            </Button>
          </div>
        </div>
      </CalendarFooterContainer>
    );
  };

  function renderMonths() {
    return months.map(month => (
      <option key={month.number} value={month.number}>
        {month.longName}
      </option>
    ));
  }

  return (
    <DatePicker
      open={open}
      minDate={subDays(startDate, 90)}
      maxDate={addDays(startDate, 90)}
      onClickOutside={() => setOpen(false)}
      onInputClick={() => setOpen(true)}
      onChange={onChange}
      startDate={startDate}
      endDate={endDate}
      selectsRange
      showPreviousMonths
      monthsShown={2}
      calendarContainer={CalendarContainerComponent}
      customInput={
        <FormControl
          variant="outlined"
          style={{
            background: startDate || endDate ? colors.lightGray07 : undefined,
          }}>
          <TextField value={startDate} />
        </FormControl>
      }
      renderCustomHeader={({
        customHeaderCount,
        changeYear,
        changeMonth,
        monthDate,
      }) => (
        <DatePickerCustomHeader>
          {customHeaderCount === 0 ? (
            <>
              <div className="start-end-labels">
                From: {new Date(startDate).getDate()}
              </div>

              <FormControl width={80}>
                <NativeSelect
                  value={
                    startMonth !== undefined
                      ? startMonth
                      : new Date(monthDate).getMonth()
                  }
                  disableUnderline
                  onChange={event => {
                    changeMonth(Number(event?.target?.value) + 1);
                    // @ts-ignore
                    setStartMonth(Number(event?.target?.value));
                  }}>
                  {/* @ts-ignore */}
                  {renderMonths()}
                </NativeSelect>
              </FormControl>

              <FormControl width={65}>
                <NativeSelect
                  disableUnderline
                  defaultValue={currentYear}
                  onChange={event => {
                    changeYear(Number(event?.target?.value));
                  }}>
                  {yearsList.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </NativeSelect>
              </FormControl>
            </>
          ) : (
            <>
              <div className="start-end-labels">
                To: {endDate !== null && new Date(endDate).getDate()}
              </div>

              <FormControl width={80}>
                <NativeSelect
                  disableUnderline
                  value={
                    endMonth !== undefined
                      ? endMonth
                      : new Date(monthDate).getMonth()
                  }
                  onChange={event => {
                    const aux = Number(event?.target?.value) - 1;
                    changeMonth(aux);
                    // @ts-ignore
                    setEndMonth(Number(event?.target?.value));
                  }}>
                  {/* @ts-ignore */}
                  {renderMonths(monthDate)}
                </NativeSelect>
              </FormControl>

              <FormControl width={65}>
                <NativeSelect
                  disableUnderline
                  defaultValue={currentYear}
                  onChange={event => {
                    changeYear(Number(event?.target?.value));
                  }}>
                  {yearsList.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </NativeSelect>
              </FormControl>
            </>
          )}
        </DatePickerCustomHeader>
      )}
    />
  );
}
